<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue';
import ReportTestingMethodsFilters from '@/_core/entities/report/ui/ReportTestingMethodsFilters/ReportTestingMethodsFilters.vue';
import ComplianceFilterDropdown from '@/_core/widgets/ComplianceFilterDropdown/ComplianceFilterDropdown.vue';
import type { TComplianceSummary } from '@/_core/entities/report/models/types';
import ReportVulnerabilityListItem from '@/_core/entities/report/ui/ReportVulnerabilityListItem/ReportVulnerabilityListItem.vue';
import ReportVulnerabilityListItemSkeleton from '@/_core/entities/report/ui/ReportVulnerabilityListItem/ReportVulnerabilityListItemSkeleton.vue';
import ReportPagination from '@/_core/entities/report/ui/ReportPagination/ReportPagination.vue';
import type {
  TFolder,
  TReportPageResponse,
  TTestingMethod,
} from '@/_core/entities/report/models/types';
import type { TVulnerability } from '@/_core/entities/vulnerability/models/types';
import {
  parseTestingMethod,
  useActiveFolderName,
} from '@/_core/entities/report/utils/report.utils';
import { trackReportTabSwitched } from '@/services/analytics';
import {
  DEFAULT_FOLDER_NAMES,
  DEFAULT_TESTING_METHOD,
  REPORT_PAGE_LIMIT,
  REPORT_ROUTE_NAMES,
} from '@/_core/entities/report/models/consts';
import { getQueryParamValue } from '@/_core/shared/utils/getQueryParamValue';
import { RawLocation } from 'vue-router';
import { useMoveVulnerabilityToFolder } from '@/_core/entities/vulnerability/api/useCreateFolderAndMove';
import { useFalsePositiveFolder } from '@/_core/entities/vulnerability/utils/useFalsePositiveFolder';
import { buildMoveFolderPayload } from '@/_core/entities/vulnerability/utils/buildMovePayload';
import { useToast } from '@/_core/shared/ui/AppToast/useToast';

const props = defineProps<{
  setPage(page: number): void;
  currentPage: number;
  totalPages: number;
  foldersData?: TFolder[];
  pageData?: TReportPageResponse;
  isDataLoading: boolean;
  isFolderEmpty?: boolean;
  complianceSummary?: TComplianceSummary;
  selectedRequirementId?: string;
  selectedItemId?: string;
}>();

const emit = defineEmits<{
  (e: 'complianceSelect', payload: { requirementId?: string; itemId?: string }): void;
  (e: 'complianceClear'): void;
}>();

const instance = getCurrentInstance();
const route = computed(() => instance?.proxy.$route);

const activeFolderName = useActiveFolderName(route);

const activeFolder = computed(() =>
  props.foldersData?.find((f) => f.name === activeFolderName.value)
);

const isInFalsePositivesFolder = computed(
  () => activeFolderName.value === DEFAULT_FOLDER_NAMES.falsePositives
);

const testingMethodCounts = computed(
  () => activeFolder.value?.testingMethodCounts ?? { all: 0, sast: 0, dast: 0, sastDast: 0 }
);

const activeTestingMethod = computed(() => {
  return parseTestingMethod(
    getQueryParamValue(route.value?.query?.testingMethod, DEFAULT_TESTING_METHOD)
  );
});

const skeletonCount = computed(() => {
  const method = activeTestingMethod.value;
  const countKey = method === 'sast-dast' ? 'sastDast' : method;
  const count = testingMethodCounts.value[countKey];
  return count > 0 && count < REPORT_PAGE_LIMIT ? count : REPORT_PAGE_LIMIT;
});

const toast = useToast();
const { mutateAsync: moveVulnerability } = useMoveVulnerabilityToFolder();
const falsePositiveFolder = useFalsePositiveFolder(computed(() => props.foldersData ?? []));

const mainReportFolder = computed(
  () => props.foldersData?.find((f) => f.name === DEFAULT_FOLDER_NAMES.mainReport) ?? null
);

async function moveVulnerabilityToTarget(
  vulnerability: TVulnerability,
  folderName: string,
  holderId: string
) {
  const params = route.value?.params;
  const payload = params
    ? buildMoveFolderPayload(params, vulnerability.uid, holderId, folderName)
    : null;

  if (!payload) return;

  try {
    await moveVulnerability(payload);

    toast.success({ message: `Vulnerability moved to "${folderName}".` });
  } catch {
    toast.error({ message: 'Failed to move vulnerability' });
  }
}

function handleFalsePositive(vulnerability: TVulnerability) {
  const folder = falsePositiveFolder.value;
  if (!folder) return;
  moveVulnerabilityToTarget(vulnerability, folder.name, folder.holderId);
}

function handleRemoveFromFalsePositives(vulnerability: TVulnerability) {
  const folder = mainReportFolder.value;
  if (!folder) return;
  moveVulnerabilityToTarget(vulnerability, folder.name, folder.holderId);
}

function getVulnerabilityTo(uid: string): RawLocation {
  const params = route.value?.params;

  if (params?.jobId) {
    return {
      name: REPORT_ROUTE_NAMES.singleScanVulnerability,
      params: { jobId: params.jobId, uid },
    };
  }

  return {
    name: REPORT_ROUTE_NAMES.versionVulnerability,
    params: {
      integrationId: params?.integrationId ?? '',
      branchName: params?.branchName ?? '',
      versionId: params?.versionId ?? '',
      uid,
    },
  };
}

function handleTestingMethodFilterChange(method: TTestingMethod) {
  trackReportTabSwitched(undefined, undefined, method);
  instance?.proxy.$router.push({
    query: {
      ...instance.proxy.$route.query,
      testingMethod: method,
    },
  });

  if (props.currentPage === 1) return;

  props.setPage(1);
}
</script>

<template>
  <div>
    <div class="tw:typography-h1">Vulnerabilities in the code</div>

    <div
      class="tw:mt-4 tw:lg:mt-6 tw:flex tw:flex-wrap tw:items-center tw:gap-4 tw:justify-between"
    >
      <ReportTestingMethodsFilters
        :initial-value="activeTestingMethod"
        :testing-method-counts="testingMethodCounts"
        @filterChanged="handleTestingMethodFilterChange($event)"
      />
      <ComplianceFilterDropdown
        :summary="complianceSummary"
        :selectedRequirementId="selectedRequirementId"
        :selectedItemId="selectedItemId"
        @select="emit('complianceSelect', $event)"
        @clear="emit('complianceClear')"
      />
    </div>

    <div
      v-if="isFolderEmpty"
      class="tw:mt-4 tw:text-secondary tw:typography-small tw:min-h-96 tw:grid tw:place-items-center"
    >
      No vulnerabilities found. Try different filters or check other folders.
    </div>

    <div v-else class="tw:mt-4 tw:overflow-x-auto tw:max-sm:-mr-4">
      <div class="tw:min-w-157">
        <div class="tw:px-4 tw:flex tw:gap-4 tw:lg:gap-6">
          <div class="tw:text-secondary tw:typography-small tw:w-20 tw:shrink-0 tw:truncate">
            ID
          </div>
          <div class="tw:text-secondary tw:typography-small tw:w-25 tw:shrink-0">Severity</div>
          <div
            class="tw:text-secondary tw:typography-small tw:max-xl:w-52.5 tw:grow tw:shrink-0 tw:basis-auto tw:truncate"
          >
            Category
          </div>

          <div class="tw:flex tw:gap-4 tw:lg:gap-6">
            <div
              class="tw:text-secondary tw:typography-small tw:flex-1 tw:pr-1.5 tw:whitespace-nowrap"
            >
              Method
            </div>
            <div
              class="tw:text-secondary tw:typography-small tw:grow-0 tw:shrink-0 tw:basis-6"
            ></div>
          </div>
        </div>

        <div class="tw:mt-4 tw:flex tw:flex-col tw:gap-2">
          <template v-if="isDataLoading">
            <ReportVulnerabilityListItemSkeleton v-for="i in skeletonCount" :key="i" />
          </template>
          <template v-else>
            <ReportVulnerabilityListItem
              v-for="vulnerability in pageData?.vulnerabilities"
              :key="vulnerability.id"
              :vulnerability="vulnerability"
              :to="getVulnerabilityTo(vulnerability.uid)"
              :is-in-false-positives-folder="isInFalsePositivesFolder"
              @mark-as-false-positive="handleFalsePositive(vulnerability)"
              @remove-from-false-positives="handleRemoveFromFalsePositives(vulnerability)"
            />
          </template>
        </div>

        <ReportPagination
          class="tw:mt-4"
          :current-page="currentPage"
          :total-pages="totalPages"
          @pageChange="setPage($event)"
        />
      </div>
    </div>
  </div>
</template>
